import { ref, readonly } from 'vue';
import { useAuthentication } from '@/composables/useAuthentication';
import { buildUrlString } from '@/utils/buildUrlString';
import { loadExternalCodeFile } from '@/utils/loadExternalCodeFile';

const FREESTAR_ADS = {
  RIGHT_1: { id: 'stockcharts_300x250_300x600_Right_1', dimensions: '__300x250 __300x250' },
  IN_CONTENT_MREC_PRIMIS_REPLACEMENT_CHART: { id: 'stockcharts_mrecprimisreplacement', dimensions: '__300x300' },
  RIGHT_2: { id: 'stockcharts_300x250_300x600_Right_2', dimensions: '__300x250 __300x250' },
  ATF: { id: 'StockCharts_728x90_970x90_970x250_320x50_ATF', dimensions: '__320x50 __728x90' },
  RIGHT_SIDEWALL: { id: 'stockcharts_right_sidewall', dimensions: '__300x600' },
  BTF_LEADERBOARD: { id: 'stockcharts_btfleaderboard', dimensions: '__970x250' }
} as const;
let isInitialized = false;

const areFreestarAdsDisplayed = ref(false);

function addFreestarEnabledSlot(adId: typeof FREESTAR_ADS[keyof typeof FREESTAR_ADS]['id']) {
  if (!areFreestarAdsDisplayed.value) {
    window.sc3WorkbenchLogger.error(`Unable to add Freestar EnabledSlot: ${adId} - Freestar ads are not displayed.`);
    return;
  }

  window.freestar.config.enabled_slots.push({
    placementName: adId,
    slotId: adId
  });
}

async function init() {
  const { isLoggedIn } = useAuthentication();

  if (isInitialized || isLoggedIn.value) {
    return;
  }

  isInitialized = true;

  const freestarScriptUrl = buildUrlString('/vendor/freestar/10.25.2024/freestar.js', { domain: 'd.stockcharts.com' });

  try {
    await loadExternalCodeFile(freestarScriptUrl, 3, 'js');

    if (!('initializeFreestar' in window)) {
      throw new Error('window does not have property "initializeFreestar".');
    }

    if (typeof window.initializeFreestar !== 'function') {
      throw new Error(`window.initializeFreestar is not a function: ${typeof window.initializeFreestar}.`);
    }

    window.initializeFreestar();

    areFreestarAdsDisplayed.value = true;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to initialize Freestar ads.', e);
  }
}

export function useFreestarAds() {
  const { addMemberAuthenticationCallback } = useAuthentication();

  addMemberAuthenticationCallback(init);

  return {
    FREESTAR_ADS,
    areFreestarAdsDisplayed: readonly(areFreestarAdsDisplayed),
    addFreestarEnabledSlot
  };
}
